/**
 * React Query hooks for WL-3 (fieldiq-api#1339): the end-of-job service report.
 *
 * Keys start ['wirelineSlickline'] and every write invalidates that prefix, so
 * a report change also refreshes the job page's report panel and the register.
 * Every read spreads LOAD_FAILURE_QUERY_OPTIONS: a refusal reads as a refusal,
 * never as an empty list. A write returns its warnings; a refusal arrives as
 * the mutation's error.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { LOAD_FAILURE_QUERY_OPTIONS } from '../lib/load-failure';
import type { WlWarningEntry, WlWriteResult } from '../types/wireline-slickline-jobs';
import type { WlReport, WlReportDetail, WlReportTextWrite } from '../types/wireline-slickline-reports';
import { invalidateWl, unwrapWl, unwrapWlList, wlKeys } from './useWirelineSlickline';

export const wlReportKeys = {
  list: (p: { jobId?: string; status?: string }) => [...wlKeys.all, 'reports', p.jobId ?? '', p.status ?? ''] as const,
  report: (id: string) => [...wlKeys.all, 'report', id] as const,
};

const writeResult = <T>(body: unknown): WlWriteResult<T> => {
  const { data, meta } = unwrapWl<T>(body);
  const w = body && typeof body === 'object' ? (body as { warnings?: unknown }).warnings : undefined;
  return { data, warnings: Array.isArray(w) ? (w as WlWarningEntry[]) : [], meta };
};

export function useWlReports(filters: { jobId?: string; status?: string } = {}, enabled = true) {
  return useQuery({
    queryKey: wlReportKeys.list(filters),
    queryFn: async () => {
      const params: { job_id?: string; status?: string } = {};
      if (filters.jobId) params.job_id = filters.jobId;
      if (filters.status) params.status = filters.status;
      return unwrapWlList<WlReport>((await api.wirelineSlickline.reports.list(Object.keys(params).length ? params : undefined)).data);
    },
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useWlReport(id: string | undefined) {
  return useQuery({
    queryKey: wlReportKeys.report(id ?? ''),
    queryFn: async () => unwrapWl<WlReportDetail>((await api.wirelineSlickline.reports.get(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

function useWlReportWrite<V>(fn: (v: V) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: V) => writeResult<WlReportDetail>(await fn(v)),
    onSuccess: () => invalidateWl(qc),
  });
}

export const useCreateWlReport = () =>
  useWlReportWrite(async ({ jobId, body }: { jobId: string; body?: WlReportTextWrite }) =>
    (await api.wirelineSlickline.reports.create(jobId, body ?? {})).data);
export const useRefreshWlReport = () =>
  useWlReportWrite(async ({ id, body }: { id: string; body?: WlReportTextWrite }) =>
    (await api.wirelineSlickline.reports.refresh(id, body ?? {})).data);
export const useIssueWlReport = () =>
  useWlReportWrite(async (id: string) => (await api.wirelineSlickline.reports.issue(id)).data);
export const useVoidWlReport = () =>
  useWlReportWrite(async ({ id, reason }: { id: string; reason: string }) =>
    (await api.wirelineSlickline.reports.voidReport(id, { reason })).data);
